import React from 'react';
import StepIndicator from './StepIndicator';
import { useTranslation } from 'react-i18next';

function TooMuchScreen({
  onBack,
  onClose,
  option,
  onSelectOption,
  pauseDate,
  onDateChange,
  minDate,
  optionDates,
  onConfirm,
  onCancelAnyway,
  isSubmitting,
}) {
  const { t } = useTranslation();
  const isSkipOne = option === 'skip_1';
  const isSkipTwo = option === 'skip_2';
  const isDate = option === 'date';
  const isDateValid = !minDate || !pauseDate || pauseDate >= minDate;
  const canConfirm =
    option === 'skip_1' || option === 'skip_2' || (option === 'date' && pauseDate && isDateValid);

  return (
    <>
      <StepIndicator step={2} onBack={onBack} showBack onClose={onClose} />

      <div className="t-mb-4">
        <h2 className="t-text-[26px] t-font-serif t-text-slate-900 t-leading-tight t-mb-2">
          {t('CancelFlow_TooMuch_Title_Line1')}
          <br />
          <span>
            {t('CancelFlow_TooMuch_Title_Line2').split(' ').slice(0, -1).join(' ')}{' '}
            <em className="t-text-secondary">
              {t('CancelFlow_TooMuch_Title_Line2').split(' ').slice(-1).join(' ')}
            </em>
          </span>
        </h2>
        <p className="t-text-sm t-text-slate-600 t-m-0">{t('CancelFlow_TooMuch_Subtitle')}</p>
      </div>

      <div className="t-flex t-flex-col t-gap-2 t-mb-3">
        <button
          type="button"
          onClick={() => onSelectOption('skip_1')}
          className={`t-flex t-items-center t-gap-3 t-px-4 t-py-3 t-rounded-xl t-border t-transition
          ${
            isSkipOne
              ? 't-border-secondary t-bg-teal-bg'
              : 't-border-slate-200 hover:t-border-secondary hover:t-bg-teal-bg'
          }`}
        >
          <div
            className={`t-w-10 t-h-10 t-rounded-xl t-flex t-items-center t-justify-center t-bg-teal-bg t-text-secondary t-shrink-0 t-transition ${
              isSkipOne ? '!t-bg-secondary t-text-white' : ''
            }`}
          >
            <span className="t-text-base">⏸</span>
          </div>
          <div className="t-flex-1 t-text-left">
            <div className="t-text-sm t-font-semibold t-text-slate-900">
              {t('CancelFlow_TooMuch_SkipTitle')}
            </div>
            {optionDates?.skip_1 && (
              <div className="t-mt-1 t-text-xs t-text-slate-600">
                {t('CancelFlow_TooMuch_NextShipmentOn', { date: optionDates.skip_1 })}
              </div>
            )}
          </div>
          <div
            className={`t-w-5 t-h-5 t-rounded-full t-border t-flex t-items-center t-justify-center t-text-xs t-transition ${
              isSkipOne
                ? 't-bg-secondary t-border-secondary t-text-white'
                : 't-border-slate-300 t-text-transparent'
            }`}
          >
            ✓
          </div>
        </button>

        <button
          type="button"
          onClick={() => onSelectOption('skip_2')}
          className={`t-flex t-items-center t-gap-3 t-px-4 t-py-3 t-rounded-xl t-border t-transition
          ${
            isSkipTwo
              ? 't-border-secondary t-bg-teal-bg'
              : 't-border-slate-200 hover:t-border-secondary hover:t-bg-teal-bg'
          }`}
        >
          <div
            className={`t-w-10 t-h-10 t-rounded-xl t-flex t-items-center t-justify-center t-bg-teal-bg t-text-secondary t-shrink-0 t-transition ${
              isSkipTwo ? '!t-bg-secondary t-text-white' : ''
            }`}
          >
            <span className="t-text-base">⏸</span>
          </div>
          <div className="t-flex-1 t-text-left">
            <div className="t-text-sm t-font-semibold t-text-slate-900">
              {t('CancelFlow_TooMuch_Skip3Title')}
            </div>
            {optionDates?.skip_2 && (
              <div className="t-mt-1 t-text-xs t-text-slate-600">
                {t('CancelFlow_TooMuch_NextShipmentOn', { date: optionDates.skip_2 })}
              </div>
            )}
          </div>
          <div
            className={`t-w-5 t-h-5 t-rounded-full t-border t-flex t-items-center t-justify-center t-text-xs t-transition ${
              isSkipTwo
                ? 't-bg-secondary t-border-secondary t-text-white'
                : 't-border-slate-300 t-text-transparent'
            }`}
          >
            ✓
          </div>
        </button>

        <button
          type="button"
          onClick={() => onSelectOption('date')}
          className={`t-flex t-items-center t-gap-3 t-px-4 t-py-3 t-rounded-xl t-border t-transition
          ${
            isDate
              ? 't-border-secondary t-bg-teal-bg'
              : 't-border-slate-200 hover:t-border-secondary hover:t-bg-teal-bg'
          }`}
        >
          <div
            className={`t-w-10 t-h-10 t-rounded-xl t-flex t-items-center t-justify-center t-bg-teal-bg t-text-secondary t-shrink-0 t-transition ${
              isDate ? '!t-bg-secondary t-text-white' : ''
            }`}
          >
            <span className="t-text-base">📅</span>
          </div>
          <div className="t-flex-1 t-text-left">
            <div className="t-text-sm t-font-semibold t-text-slate-900">
              {t('CancelFlow_TooMuch_DateTitle')}
            </div>
            <div className="t-text-xs t-text-slate-600">
              {pauseDate
                ? t('CancelFlow_TooMuch_DateSubtitle_Filled', {
                    date: new Date(pauseDate + 'T00:00:00').toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }),
                  })
                : t('CancelFlow_TooMuch_DateSubtitle_Empty')}
            </div>
          </div>
          <div
            className={`t-w-5 t-h-5 t-rounded-full t-border t-flex t-items-center t-justify-center t-text-xs t-transition ${
              isDate
                ? 't-bg-secondary t-border-secondary t-text-white'
                : 't-border-slate-300 t-text-transparent'
            }`}
          >
            ✓
          </div>
        </button>
      </div>

      {isDate && (
        <div className="t-mb-3">
          <input
            type="date"
            className="t-w-full t-px-3 t-py-2.5 t-rounded-xl t-border t-border-teal-light t-bg-teal-bg t-text-sm t-text-slate-800 focus:t-outline-none focus:t-border-secondary"
            value={pauseDate}
            min={minDate || undefined}
            onChange={(e) => onDateChange(e.target.value)}
          />
        </div>
      )}

      <div className="t-flex t-items-center t-gap-2 t-bg-teal-bg t-border-l-4 t-border-secondary t-rounded-lg t-px-3 t-py-2 t-text-[12px] t-text-slate-700 t-mb-4">
        <span className="t-text-secondary">🛡</span>
        <span>{t('CancelFlow_TooMuch_Info')}</span>
      </div>

      <button
        type="button"
        onClick={onConfirm}
        disabled={!canConfirm || isSubmitting}
        className="t-w-full t-inline-flex t-items-center t-justify-center t-rounded-xl t-px-4 t-py-3 t-bg-secondary t-text-white t-font-semibold t-text-sm t-gap-2 t-shadow-sm hover:t-shadow-[0_4px_12px_rgba(26,107,90,0.28)] t-transition t-mb-2 disabled:t-opacity-50 disabled:t-cursor-not-allowed"
      >
        <span>{t('CancelFlow_TooMuch_Confirm')}</span>
        <span aria-hidden="true">✓</span>
      </button>

      <button
        type="button"
        onClick={onCancelAnyway}
        disabled={isSubmitting}
        className="t-block t-w-full t-text-center t-text-sm t-text-slate-600 hover:t-text-slate-800"
      >
        {t('CancelFlow_TooMuch_CancelAnyway')}
      </button>
    </>
  );
}

export default TooMuchScreen;
